import React, { useState } from 'react';
import { cn } from '../../utils/cn';
import { useLocation } from 'react-router-dom';
import { ILMSPageHeader } from '../../components/ILMSPageHeader';
import { tabVisible } from '../../components/ILMSPageAccessPicker';
import { useAuth } from '../../hooks/useAuth';

export interface TabDef {
  id: string;
  label: string;
  render: () => React.ReactElement;
}

/**
 * Shared shell for the many v2 modules that group several related tables as
 * tabs on one page (e.g. Breeding = Heat/Service/Pregnancy/Calving/KPIs).
 * Keeps the page-header + tab-bar markup consistent without repeating it in
 * every module page (the pattern was previously hand-rolled per-page).
 */
export function TabbedPage({
  title,
  subtitle,
  actions,
  tabs,
  defaultTab,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  tabs: TabDef[];
  defaultTab?: string;
}) {
  // A user given only some of this page's tabs (Page Access) sees just those.
  const { user } = useAuth();
  const { pathname } = useLocation();
  tabs = tabs.filter((t) => tabVisible(user?.restricted_modules, pathname, t.id));
  const [tab, setTab] = useState(defaultTab ?? tabs[0]?.id);
  const active = tabs.find((t) => t.id === tab) ?? tabs[0];

  return (
    <div className="flex flex-col gap-4">
      <ILMSPageHeader title={title} subtitle={subtitle} actions={actions} />
      {/* flex-wrap (not horizontal-scroll-only) - a page with enough tabs to
          overflow one row used to hide the later ones behind an
          undiscoverable scroll with no visible scrollbar; wrapping keeps
          every tab always visible instead. */}
      <div className="flex flex-wrap gap-1 border-b border-ink-300/15">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-3.5 h-11 text-sm font-medium border-b-2 whitespace-nowrap shrink-0',
              active?.id === t.id ? 'border-brand-green-600 text-brand-green-700' : 'border-transparent text-ink-500 hover:text-ink-700'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {active?.render()}
    </div>
  );
}
